/**
 * Every action Civil can take, named.
 *
 * CLAUDE.md's agent-first rule: the keyboard dispatches into this registry and an
 * agent will dispatch into the same one. An action reachable only by clicking a
 * particular button is invisible to an agent, so actions live here and buttons call
 * into them rather than the other way round.
 *
 * A command carries a plain-language description because that is what an agent
 * matches an instruction against, and it returns what it did in words because that
 * string is what the user is shown — by the toast now, by an agent transcript later.
 */

export type CommandId =
  | 'nav.home'
  | 'nav.ascend'
  | 'nav.descend'
  | 'project.switch'
  | 'project.settings'
  | 'access.manage'
  | 'prompts.manage'
  | 'pane.tree'
  | 'pane.inspector'
  | 'canvas.fit'
  | 'view.next'
  | 'view.tldr'
  | 'view.chat'
  | 'view.files'
  | 'view.ui'
  | 'ui.request.new'
  | 'ui.snapshot.add'
  | 'ui.capture'
  | 'ui.request.match'
  | 'ui.request.interpret'
  | 'ui.request.copy'
  | 'ui.request.download'
  | 'ui.mode.view'
  | 'ui.mode.annotate'
  | 'ui.mode.draw'
  | 'ui.mark.remove'
  | 'view.lens'
  | 'altitude.up'
  | 'altitude.down'
  | 'altitude.story'
  | 'altitude.product'
  | 'altitude.mechanism'
  | 'tldr.regenerate'
  | 'chat.new'
  | 'node.add'
  | 'canvas.delete'
  | 'edit.undo'
  | 'canvas.clearSelection'
  | 'file.save'
  | 'run.start'
  | 'run.cancel'
  | 'session.start'
  | 'session.stop'
  | 'project.diff'
  | 'project.commit'
  | 'project.sync'
  | 'git.check'
  | 'git.branch'
  | 'git.pullRequest'
  | 'project.apply'
  | 'project.liftRepo'
  | 'help.keys';

export interface CommandContext {
  /** Where the user is, so a command can refuse when it does not apply. `diff` is
   *  the review panel: a modal, so canvas commands must not fire underneath it —
   *  mutating the project while its diff is on screen is how a commit message gets
   *  written against a diff that is no longer true. */
  where: 'home' | 'canvas' | 'code' | 'diff' | 'tldr' | 'chat' | 'files' | 'ui';
  hasSelection: boolean;
  pendingCount: number;
  canCommit: boolean;
  /** Whether the op history has anything to walk back. */
  canUndo: boolean;
  /** A runnable graph is on screen with nothing blocking Run. */
  canRun: boolean;
  /** A run is queued or running right now. */
  runActive: boolean;
  /** The composition canvas is on screen and valid, so Run means run the app —
   *  the session of docs/app-session.md, not a graph. */
  canSession: boolean;
  /** An app session is starting or live right now. */
  sessionActive: boolean;
  depth: number;
  /** The project already has a composition, so reading the repo updates it rather
   *  than generating one. Optional: absent reads as "no composition yet". */
  hasComposition?: boolean;
  /** Reading the repo would be refused on this project (a Python project Civil
   *  generates for, or documents still at the root). Optional: absent reads as no. */
  liftRefused?: boolean;
  /** How many views the project keeps (§21). Optional: absent reads as none. */
  viewCount?: number;
  /** The composition is on screen through a view, which groups parts but never
   *  adds or removes one. Optional: absent reads as no. */
  inView?: boolean;
  /** The Architecture tab is on screen, so its lenses apply (§25). Optional: absent reads as no. */
  inArchitecture?: boolean;
  /**
   * The flow's altitudes, highest first, and the one on screen (docs/altitude.md).
   * Optional: absent reads as a flow without altitudes, or another view.
   */
  altitudes?: ('story' | 'product' | 'mechanism')[];
  altitude?: 'story' | 'product' | 'mechanism';
  /** The signed-in user may manage access (prd-deltas §22). Optional: absent reads as no. */
  isAdmin?: boolean;
  /** Whether the project panel (left) and the inspector (right) are on screen, so
   *  their commands can say "Hide" or "Show". Optional: absent reads as shown. */
  treeShown?: boolean;
  inspectorShown?: boolean;
  /** The UI tab has a snapshot open, and a mark on it selected (docs/ui-tab.md). Optional: absent reads as no. */
  uiHasSnapshot?: boolean;
  uiHasSelectedMark?: boolean;
}

/**
 * What a command did, in words. Undefined means it declined and nothing happened —
 * which is worth a toast, because a keystroke that silently does nothing reads as a
 * bug. Null means it acted and the effect speaks for itself on screen — deleting the
 * selected node is watched happening, so no toast repeats it.
 *
 * The object form is for a command that keeps working after it returns: its toast
 * stays (`sticky`) and follows the work's progress, named by `live`, in place —
 * reading a repository takes minutes, and a toast gone in a second says nothing.
 */
export type CommandOutcome =
  | string
  | { detail: string; sticky?: boolean; live?: string }
  | null
  | undefined;

export interface Command {
  id: CommandId;
  title: string;
  /** Plain language, present tense. An agent matches instructions against this. */
  description: string;
  /** Chord in the form used by `matches` below, e.g. "mod+k" or "shift+?". */
  keys: string[];
  /** Whether it applies right now. A command that cannot run is not offered. */
  enabled: (context: CommandContext) => boolean;
  /** When the name depends on the project's state — the same verb reads "Generate"
   *  on an empty project and "Update" on one that has documents. `title` is the
   *  name with no context at all (the keyboard list). */
  titleIn?: (context: CommandContext) => string;
}

/** The command's name as it should read right now. */
export const titleOf = (command: Command, context: CommandContext): string =>
  command.titleIn?.(context) ?? command.title;

/**
 * `mod` is Cmd on Apple platforms and Ctrl elsewhere, so a binding is written once.
 */
const isApple = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

export const MOD_LABEL = isApple ? '⌘' : 'Ctrl';

export function formatChord(chord: string): string {
  return chord
    .split('+')
    .map((part) => {
      switch (part) {
        case 'mod': return MOD_LABEL;
        case 'shift': return isApple ? '⇧' : 'Shift';
        case 'alt': return isApple ? '⌥' : 'Alt';
        case 'escape': return 'Esc';
        case 'enter': return '↵';
        case 'arrowup': return '↑';
        case 'arrowdown': return '↓';
        default: return part.length === 1 ? part.toUpperCase() : part;
      }
    })
    .join(isApple ? '' : '+');
}

export function matches(chord: string, event: KeyboardEvent): boolean {
  const parts = chord.split('+');
  const key = parts[parts.length - 1]!;
  const wantMod = parts.includes('mod');
  const wantShift = parts.includes('shift');
  const wantAlt = parts.includes('alt');

  const hasMod = isApple ? event.metaKey : event.ctrlKey;
  if (hasMod !== wantMod) return false;
  if (event.altKey !== wantAlt) return false;
  // Shift is implied by punctuation like `?`, so it is only checked when the binding
  // asks for it — otherwise "shift+/" would never match a keyboard that types ? that way.
  if (wantShift && !event.shiftKey) return false;
  // But for plain letters and digits an unrequested Shift is a different chord:
  // without this, Cmd+Shift+Z — redo everywhere else — ran undo.
  if (!wantShift && event.shiftKey && /^[a-z0-9]$/.test(key)) return false;

  return event.key.toLowerCase() === key.toLowerCase();
}

export const COMMANDS: readonly Command[] = [
  {
    id: 'nav.home',
    title: 'All projects',
    description: 'Leave the current project and show every project you can open.',
    keys: ['mod+shift+o'],
    enabled: (c) => c.where !== 'home',
  },
  {
    id: 'project.switch',
    title: 'Switch project',
    description: 'Open the project picker to change project or open a repository.',
    keys: ['mod+p'],
    enabled: (c) => c.where !== 'home',
  },
  {
    id: 'nav.ascend',
    title: 'Ascend',
    description: 'Leave this context and return to the one that contains it.',
    keys: ['escape'],
    enabled: (c) => c.depth > 0,
  },
  {
    id: 'nav.descend',
    title: 'Descend',
    description: 'Enter the selected node — its canvas if it is a graph, its code if it is code.',
    keys: ['enter'],
    enabled: (c) => c.where === 'canvas' && c.hasSelection,
  },
  {
    id: 'canvas.fit',
    title: 'Fit to view',
    description: 'Frame every node on the current canvas.',
    keys: ['f'],
    enabled: (c) => c.where === 'canvas',
  },
  {
    id: 'view.next',
    title: 'Next view',
    description:
      'Go to the next tab of the project, in order: the TL;DR, the flow (what the ' +
      'application does), the architecture (what it is made of), the chat, the files.',
    keys: ['v'],
    enabled: (c) => c.where === 'canvas' || c.where === 'tldr' || c.where === 'chat' || c.where === 'files',
  },
  {
    id: 'view.tldr',
    title: 'TL;DR',
    description:
      'Show what the application is, in at most four sentences, written from its documents. ' +
      'It is where a project opens.',
    keys: ['t'],
    enabled: (c) => c.where !== 'home' && c.where !== 'diff',
  },
  {
    id: 'view.chat',
    title: 'Chat',
    description:
      'Ask questions about the application. Answers name its parts and cite its code; ' +
      'nothing asked there changes the project.',
    keys: ['c'],
    enabled: (c) => c.where !== 'home' && c.where !== 'diff',
  },
  {
    id: 'view.files',
    title: 'File Browser',
    description: 'Show the repository\'s files beside the code editor. Files opened from anywhere land here.',
    keys: ['e'],
    enabled: (c) => c.where !== 'home' && c.where !== 'diff',
  },
  {
    id: 'view.ui',
    title: 'UI',
    description:
      'Mark up the running application: gather snapshots of a frontend into a request, ' +
      'pin notes to places on them and draw on them. Nothing there changes the code.',
    keys: ['i'],
    enabled: (c) => c.where !== 'home' && c.where !== 'diff',
  },
  {
    id: 'ui.request.new',
    title: 'New UI request',
    description: 'Start a request for the chosen frontend: a set of snapshots and the marks on them.',
    keys: [],
    enabled: (c) => c.where === 'ui',
  },
  {
    id: 'ui.snapshot.add',
    title: 'Add a screenshot',
    description: 'Add a screenshot of the application to the open request, to mark up. Pasting or dropping one does the same.',
    keys: [],
    enabled: (c) => c.where === 'ui',
  },
  {
    id: 'ui.capture',
    title: 'Open with Civil',
    description:
      'Open the chosen frontend where it runs, in a new tab with Civil’s overlay on it, through Civil’s Chrome extension: ' +
      'mark it up right on the page, and every snapshot, mark and note arrives in the open request as it is made.',
    keys: ['k'],
    enabled: (c) => c.where === 'ui',
  },
  {
    id: 'ui.request.match',
    title: 'Match marks to code',
    description:
      'Find where each mark in the open request is in the code: the page its address is, and the files ' +
      'that write the words at each mark — each match with its file and line, or labelled a guess.',
    keys: ['m'],
    enabled: (c) => c.where === 'ui' && !!c.uiHasSnapshot,
  },
  {
    id: 'ui.request.interpret',
    title: 'Interpret the request',
    description:
      'Say what each mark in the open request asks — understood, where in the code, the change, and any ' +
      'question — for the engineer to read and correct before anything acts on it.',
    keys: [],
    enabled: (c) => c.where === 'ui' && !!c.uiHasSnapshot,
  },
  {
    id: 'ui.request.copy',
    title: 'Copy the request as a prompt',
    description: 'Copy the open request as one Markdown prompt for any agent: snapshots, notes, evidence and the interpretation as corrected.',
    keys: [],
    enabled: (c) => c.where === 'ui' && !!c.uiHasSnapshot,
  },
  {
    id: 'ui.request.download',
    title: 'Download the request for an agent',
    description: 'Download the open request as a .zip: the prompt as request.md and each snapshot with its marks drawn on.',
    keys: [],
    enabled: (c) => c.where === 'ui' && !!c.uiHasSnapshot,
  },
  {
    id: 'ui.mode.view',
    title: 'View the snapshot',
    description: 'Look at the snapshot without marking it; tapping a number selects its note.',
    keys: ['1'],
    enabled: (c) => c.where === 'ui' && !!c.uiHasSnapshot,
  },
  {
    id: 'ui.mode.annotate',
    title: 'Annotate',
    description: 'Tap the snapshot to pin a note to that place (to the element there, when Civil knows it); drag to mark a region.',
    keys: ['2'],
    enabled: (c) => c.where === 'ui' && !!c.uiHasSnapshot,
  },
  {
    id: 'ui.mode.draw',
    title: 'Draw',
    description: 'Draw on the snapshot with a pen, an arrow, a box or text, and say what the drawing means in its note.',
    keys: ['3'],
    enabled: (c) => c.where === 'ui' && !!c.uiHasSnapshot,
  },
  {
    id: 'ui.mark.remove',
    title: 'Remove the selected mark',
    description: 'Remove the selected note, region or drawing from the snapshot.',
    keys: ['backspace', 'delete'],
    enabled: (c) => c.where === 'ui' && !!c.uiHasSelectedMark,
  },
  {
    id: 'view.lens',
    title: 'Next lens',
    description:
      'Look at the architecture another way: its pieces, its deployables (the parts Generate ' +
      'app reads, and where they are edited), or a grouping the project keeps.',
    keys: ['l'],
    enabled: (c) => c.where === 'canvas' && c.inArchitecture === true,
  },
  {
    id: 'altitude.up',
    title: 'Up an altitude',
    description:
      'See the flow from higher up: Mechanism to Product, Product to Story. What is selected ' +
      'comes along, as what describes it up there.',
    keys: ['alt+arrowup'],
    enabled: (c) => c.where === 'canvas' && !!c.altitude && !!c.altitudes && c.altitudes.indexOf(c.altitude) > 0,
  },
  {
    id: 'altitude.down',
    title: 'Down an altitude',
    description:
      'See the flow from lower down: Story to Product, Product to Mechanism. What is selected ' +
      'comes along, as what it covers down there.',
    keys: ['alt+arrowdown'],
    enabled: (c) => c.where === 'canvas' && !!c.altitude && !!c.altitudes && c.altitudes.indexOf(c.altitude) < c.altitudes.length - 1,
  },
  {
    id: 'altitude.story',
    title: 'Story',
    description: 'Show the flow as a story: who comes to the application, what they do, and what they get, in order.',
    keys: [],
    enabled: (c) => c.where === 'canvas' && !!c.altitude && c.altitude !== 'story' && !!c.altitudes?.includes('story'),
  },
  {
    id: 'altitude.product',
    title: 'Product',
    description: "Show the flow as the product's own structure: the menu's groups and sections, and the agents that reach them.",
    keys: [],
    enabled: (c) => c.where === 'canvas' && !!c.altitude && c.altitude !== 'product' && !!c.altitudes?.includes('product'),
  },
  {
    id: 'altitude.mechanism',
    title: 'Mechanism',
    description: 'Show the flow as how work passes: agents, guards, tools and stores.',
    keys: [],
    enabled: (c) => c.where === 'canvas' && !!c.altitude && c.altitude !== 'mechanism' && !!c.altitudes?.includes('mechanism'),
  },
  {
    id: 'tldr.regenerate',
    title: 'Write the TL;DR again',
    description: 'Write the TL;DR again from the project\'s documents as they are now.',
    keys: [],
    enabled: (c) => c.where === 'tldr',
  },
  {
    id: 'chat.new',
    title: 'New conversation',
    description: 'Start a new conversation about the application. The last one is kept.',
    keys: [],
    enabled: (c) => c.where === 'chat',
  },
  {
    id: 'canvas.clearSelection',
    title: 'Clear selection',
    description: 'Deselect the current node.',
    keys: ['escape'],
    enabled: (c) => c.where === 'canvas' && c.hasSelection,
  },
  {
    id: 'node.add',
    title: 'Add node',
    description:
      'Add a node to the current canvas. The vocabulary is closed — client, service ' +
      'or process at the top level; agent, io, subgraph or code inside a graph.',
    keys: ['n'],
    // A view only groups parts: they are added where they are written, in the parts view.
    enabled: (c) => c.where === 'canvas' && !c.inView,
  },
  {
    id: 'canvas.delete',
    title: 'Delete',
    description:
      'Remove the selected node or edge from the canvas. A removed node takes its ' +
      'edges with it — a manifest is never left pointing at a node that is not there.',
    keys: ['backspace', 'delete'],
    enabled: (c) => c.where === 'canvas' && c.hasSelection,
  },
  {
    id: 'edit.undo',
    title: 'Undo',
    description:
      'Walk the last canvas edit back — the file returns to what it said before. ' +
      'Inside a file, Monaco keeps its own undo.',
    keys: ['mod+z'],
    enabled: (c) => c.where === 'canvas' && c.canUndo,
  },
  {
    id: 'file.save',
    title: 'Save',
    description: 'Write the open file as a pending change. Nothing is committed.',
    keys: ['mod+s'],
    enabled: (c) => c.where === 'code' || c.where === 'files',
  },
  {
    id: 'run.start',
    title: 'Run',
    description:
      'Run the graph on this canvas: choose an input, then watch it execute — the ' +
      'canvas animates and the event log tells the story.',
    keys: ['shift+enter'],
    enabled: (c) => c.canRun,
  },
  {
    id: 'session.start',
    title: 'Run app',
    description:
      'Run the application: start the app session — boundary server and every ' +
      'client dev process — or attach to the one already running. The preview ' +
      'pane opens on it.',
    // The same chord as graph Run, deliberately: Run is one gesture whose meaning
    // follows the altitude (docs/app-session.md). The enabled sets are disjoint,
    // so the chord never has two meanings at once.
    keys: ['shift+enter'],
    enabled: (c) => c.canSession,
  },
  {
    id: 'run.cancel',
    title: 'Cancel run',
    description: 'Ask the running graph to stop. It stops at the next node boundary.',
    keys: ['shift+escape'],
    enabled: (c) => c.runActive,
  },
  {
    id: 'session.stop',
    title: 'Stop app',
    description: 'Stop the app session: every process is stopped and its workspace removed.',
    // After run.cancel on purpose: while a graph run is live the shared chord
    // cancels the run — the session outlives moments, the run is one.
    keys: ['shift+escape'],
    enabled: (c) => c.sessionActive,
  },
  {
    id: 'project.diff',
    title: 'Review changes',
    description:
      'Show every pending change as a diff against the repository, before anything ' +
      'is committed.',
    keys: ['mod+d'],
    enabled: (c) => c.where !== 'home' && c.pendingCount > 0,
  },
  {
    id: 'project.commit',
    title: 'Commit',
    description: 'Commit every pending change to the repository.',
    keys: ['mod+enter'],
    enabled: (c) => c.pendingCount > 0 && c.canCommit,
  },
  {
    id: 'git.check',
    title: 'Check GitHub',
    description:
      'Ask GitHub whether the branch has new commits since you last synced. Reads only ' +
      '— nothing changes until you sync. Civil never checks on its own.',
    keys: ['g'],
    enabled: (c) => c.where !== 'home' && c.canCommit,
  },
  {
    id: 'git.branch',
    title: 'Branches',
    description:
      'Show the branches, to switch to one or create a new one from where you are. ' +
      'Pending edits stay with the branch they were made on.',
    keys: ['b'],
    enabled: (c) => c.where !== 'home' && c.where !== 'diff' && c.canCommit,
  },
  {
    id: 'git.pullRequest',
    title: 'Pull request',
    description:
      'Open a pull request from this branch into the branch it was cut from, or show ' +
      'the one already open.',
    keys: ['p'],
    enabled: (c) => c.where !== 'home' && c.where !== 'diff' && c.canCommit,
  },
  {
    id: 'project.sync',
    title: 'Sync',
    description:
      'Pick up commits pushed to the repository since this project was opened. Civil ' +
      'edits against a fixed commit, so it does not follow the branch on its own.',
    // Not mod+r: the browser owns that one — it is how the page reloads, and no
    // amount of preventDefault reliably wins it back. Plain R matches the other
    // single-key canvas verbs (N adds, F fits).
    keys: ['r'],
    enabled: (c) => c.where !== 'home' && c.canCommit,
  },
  {
    id: 'project.apply',
    title: 'Apply changes',
    description:
      'Generate code for what changed in the sketch since the last apply, and update ' +
      'the running app. Generated files arrive as pending changes, reviewed and ' +
      'committed like any other edit — nothing is applied unpreviewably.',
    keys: ['a'],
    // Not under the review panel: applying changes the pending set it is showing.
    enabled: (c) => c.where !== 'home' && c.where !== 'diff',
  },
  {
    id: 'project.liftRepo',
    title: 'Generate graph from repo',
    titleIn: (c) => (c.hasComposition ? 'Update graph from repo' : 'Generate graph from repo'),
    description:
      "Read the repository's own code — a NestJS server, a Vite client — and propose " +
      'Civil documents for it: the app composition, a graph per service, the registry, ' +
      'an architecture note. When the project already has them, update them and keep ' +
      'your node ids, layout, and anything you added. Everything arrives as pending ' +
      'changes to review in the diff; nothing is committed.',
    // U for update — G is Check GitHub, and R is Sync.
    keys: ['u'],
    // Not under the review panel: it changes the pending set the panel is showing. Not
    // on a project it would refuse: a Python project Civil generates code for would be
    // switched to TypeScript (Source control says why instead).
    enabled: (c) => c.where !== 'home' && c.where !== 'diff' && !c.liftRefused,
  },
  {
    id: 'project.settings',
    title: 'Settings',
    description: 'Show account and connections.',
    // Not mod+comma — that is the browser's own settings on macOS. One key to the
    // right, no collision.
    keys: ['mod+.'],
    enabled: (c) => c.where !== 'home',
  },
  {
    id: 'access.manage',
    title: 'Manage access',
    description: 'Open the admin page: let waiting people in, block or unblock accounts, and invite early testers by email.',
    // No chord: mod+shift+a is the browser's tab search, and this is rare enough to
    // reach from Settings or by name.
    keys: [],
    enabled: (c) => c.isAdmin === true,
  },
  {
    id: 'prompts.manage',
    title: 'Edit prompts',
    description:
      'Open Admin › Prompts: read every prompt Civil sends to a model, what each is for, ' +
      'and its default; edit one, see its history, or put it back to the default.',
    keys: [],
    enabled: (c) => c.isAdmin === true,
  },
  {
    id: 'pane.tree',
    title: 'Project panel',
    description:
      'Show or hide the project panel on the left: source control and the files. On a ' +
      'narrow screen it opens over the canvas.',
    keys: ['['],
    enabled: (c) => c.where !== 'home' && c.where !== 'diff',
    titleIn: (c) => (c.treeShown === false ? 'Show project panel' : 'Hide project panel'),
  },
  {
    id: 'pane.inspector',
    title: 'Inspector',
    description:
      'Show or hide the inspector on the right: what is selected, and its fields. On a ' +
      'narrow screen it opens over the canvas.',
    keys: [']'],
    enabled: (c) => c.where !== 'home' && c.where !== 'diff',
    titleIn: (c) => (c.inspectorShown === false ? 'Show inspector' : 'Hide inspector'),
  },
  {
    id: 'help.keys',
    title: 'Keyboard shortcuts',
    description: 'List every shortcut and what it does.',
    // Both spellings: `?` is what most layouts report, `shift+/` is what some
    // report and what synthetic events often send.
    keys: ['shift+?', 'shift+/'],
    enabled: () => true,
  },
];

export const commandById = (id: CommandId): Command =>
  COMMANDS.find((c) => c.id === id)!;

/** The command a key event should run, or undefined. */
export function resolve(event: KeyboardEvent, context: CommandContext): Command | undefined {
  return COMMANDS.find(
    (command) => command.enabled(context) && command.keys.some((chord) => matches(chord, event)),
  );
}
